import {
    type IncomeSplitConfigLegacy,
    PaycheckRoutingStatus,
} from '@fruitful/income-split';
import { useAtom } from 'jotai';
import React, { useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    LabelRegular,
    Sheet,
    Spinner,
    YStack,
} from 'smoothie';

import { incomeSplitAtomLegacy } from '../../state/PaycheckRoutingAtoms';
import {
    FRUITFUL_FDIC_DISCLOSURE,
    INVESTMENT_PRODUCTS_FDIC_DISCLOSURE,
} from '../Disclosures';
import { IncomeSplitAgreement } from './IncomeSplitReview';

interface SheetProps {
    isOpen: boolean;
    isUpdating: boolean;
    setIsUpdating: (isUpdating: boolean) => void;
    onSave: (status: PaycheckRoutingStatus) => Promise<void>;
    resetAndClose: () => void;
}

const EnableSheet = ({
    isOpen,
    isUpdating,
    setIsUpdating,
    onSave,
    resetAndClose,
}: SheetProps) => {
    const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);

    return (
        <Sheet
            isOpen={isOpen}
            onClose={resetAndClose}
            dismissOnOverlayPress={!isUpdating}
            title="You are turning on your Income Split"
            showCloseButton={false}
        >
            <YStack
                paddingTop={8}
                width="100%"
                maxWidth={500}
                alignSelf="center"
                gap={24}
            >
                <BodyMediumRegular color="$gray900">
                    Starting with your next deposit, transfers will process from
                    top to bottom subject to available funds.
                </BodyMediumRegular>
                <IncomeSplitAgreement
                    id="combined-accept"
                    checked={hasAcceptedAgreement}
                    setChecked={(checked) => {
                        setHasAcceptedAgreement(checked);
                    }}
                />
                <YStack gap={12}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text={isUpdating ? '' : 'Turn on Income Split'}
                        icon={isUpdating ? <Spinner /> : null}
                        onPress={async () => {
                            setIsUpdating(true);
                            await onSave(PaycheckRoutingStatus.ACTIVE);
                            setIsUpdating(false);
                        }}
                        disabled={!hasAcceptedAgreement || isUpdating}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.LARGE}
                        text="Cancel"
                        onPress={resetAndClose}
                        disabled={isUpdating}
                    />
                    <LabelRegular color="$gray900" marginTop={4}>
                        {FRUITFUL_FDIC_DISCLOSURE}
                        {'\n\n'}
                        {INVESTMENT_PRODUCTS_FDIC_DISCLOSURE}
                    </LabelRegular>
                </YStack>
            </YStack>
        </Sheet>
    );
};

const DisableSheet = ({
    isOpen,
    isUpdating,
    setIsUpdating,
    onSave,
    resetAndClose,
}: SheetProps) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={resetAndClose}
            dismissOnOverlayPress={!isUpdating}
            title="Are you sure you want to turn off your Income Split?"
            showCloseButton={false}
        >
            <YStack
                paddingTop={8}
                width="100%"
                maxWidth={500}
                alignSelf="center"
                gap={24}
            >
                <YStack gap={8}>
                    <BodyMediumRegular color="$gray900">
                        This change will take effect immediately. Any transfers
                        using these Income Split rules already in progress will
                        complete, but no new transfers will be initiated.
                    </BodyMediumRegular>
                </YStack>

                <YStack gap={12}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        tone="danger"
                        size={ButtonSize.LARGE}
                        text={isUpdating ? '' : 'Yes, turn off Income Split'}
                        icon={isUpdating ? <Spinner /> : null}
                        onPress={async () => {
                            setIsUpdating(true);
                            await onSave(PaycheckRoutingStatus.INACTIVE);
                            setIsUpdating(false);
                        }}
                        disabled={isUpdating}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.LARGE}
                        text="Cancel"
                        onPress={resetAndClose}
                        disabled={isUpdating}
                    />
                </YStack>
            </YStack>
        </Sheet>
    );
};

interface IncomeSplitToggleSheetProps {
    isOpen: boolean;
    onSave: (status: PaycheckRoutingStatus) => Promise<void>;
    onClose: () => void;
}

const IncomeSplitToggleSheet = ({
    isOpen,
    onSave,
    onClose,
}: IncomeSplitToggleSheetProps) => {
    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtomLegacy);
    const [isUpdating, setIsUpdating] = useState(false);

    // the status has already been temporarily changed by the switch
    const action =
        incomeSplit?.status === PaycheckRoutingStatus.ACTIVE
            ? 'enable'
            : 'disable';

    const resetAndClose = () => {
        setIncomeSplit({
            ...(incomeSplit as IncomeSplitConfigLegacy),
            status:
                action === 'enable'
                    ? PaycheckRoutingStatus.INACTIVE
                    : PaycheckRoutingStatus.ACTIVE,
        });
        onClose();
    };

    const Sheet = action === 'enable' ? EnableSheet : DisableSheet;

    return (
        <Sheet
            isUpdating={isUpdating}
            isOpen={isOpen}
            setIsUpdating={setIsUpdating}
            onSave={async (status: PaycheckRoutingStatus) => {
                await onSave(status);
            }}
            resetAndClose={resetAndClose}
        />
    );
};

export default IncomeSplitToggleSheet;
